import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Environment } from '@/features/environments/environments-types';
import {
  CreateGameBaseUrlInput,
  GameBaseUrl,
  UpdateGameBaseUrlInput,
} from './game-base-urls-types';

// HTTPS + URL validation mirrors apps/api/src/admin/game-base-urls/dto/game-base-url.dto.ts.
const schema = z.object({
  environment_id: z.string().uuid('Environment is required'),
  base_url: z
    .string()
    .url('Must be a valid URL')
    .startsWith('https://', 'Must be HTTPS'),
  is_active: z.boolean().default(true),
  // Admin hint only — runtime does NOT consult is_deprecated. The version-mapping
  // form shows a "Deprecated" suffix on these entries but still allows selection.
  is_deprecated: z.boolean().default(false),
});
type FormData = z.infer<typeof schema>;

interface GameBaseUrlsFormProps {
  initial?: GameBaseUrl;
  environments: Environment[];
  onSubmit: (data: CreateGameBaseUrlInput | UpdateGameBaseUrlInput) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function GameBaseUrlsForm({
  initial,
  environments,
  onSubmit,
  onCancel,
  isSubmitting,
}: GameBaseUrlsFormProps): JSX.Element {
  const isEdit = !!initial;
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema) as unknown as Resolver<FormData>,
    defaultValues: initial
      ? {
          environment_id: initial.environment_id,
          base_url: initial.base_url,
          is_active: initial.is_active,
          is_deprecated: initial.is_deprecated,
        }
      : { environment_id: '', base_url: '', is_active: true, is_deprecated: false },
  });

  useEffect(() => {
    if (initial) {
      reset({
        environment_id: initial.environment_id,
        base_url: initial.base_url,
        is_active: initial.is_active,
        is_deprecated: initial.is_deprecated,
      });
    }
  }, [initial, reset]);

  const isActive = watch('is_active');
  const isDeprecated = watch('is_deprecated');
  const selectedEnv = watch('environment_id');

  function handleFormSubmit(data: FormData): void {
    if (isEdit) {
      // RT#1: environment_id is immutable on PATCH.
      const { environment_id: _stripped, ...patch } = data;
      void onSubmit(patch as UpdateGameBaseUrlInput);
    } else {
      void onSubmit({
        environment_id: data.environment_id,
        base_url: data.base_url,
        is_active: data.is_active,
        is_deprecated: data.is_deprecated,
      } as CreateGameBaseUrlInput);
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="environment_id">Environment</Label>
        <Select
          value={selectedEnv ?? ''}
          onValueChange={(v) => setValue('environment_id', v)}
          disabled={isEdit}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select an environment" />
          </SelectTrigger>
          <SelectContent>
            {environments
              .filter((e) => e.is_active)
              .map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name} ({e.code})
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {isEdit && (
          <p className="text-xs text-muted-foreground">
            Environment is locked. To move a URL to a different env, delete and recreate.
          </p>
        )}
        {!isEdit && errors.environment_id && (
          <p className="text-sm text-destructive">{errors.environment_id.message as string}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="base_url">Base URL (HTTPS only)</Label>
        <Input id="base_url" type="url" placeholder="https://api.example.com" {...register('base_url')} />
        {errors.base_url && (
          <p className="text-sm text-destructive">{errors.base_url.message as string}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Switch
          id="is_active"
          checked={isActive}
          onCheckedChange={(c) => setValue('is_active', c)}
        />
        <Label htmlFor="is_active">Active</Label>
        <span className="text-xs text-muted-foreground">
          When off, hidden from the version-mapping form's dropdown.
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          id="is_deprecated"
          checked={isDeprecated}
          onCheckedChange={(c) => setValue('is_deprecated', c)}
        />
        <Label htmlFor="is_deprecated">Deprecated</Label>
        <span className="text-xs text-muted-foreground">
          Admin hint only. Runtime does NOT consult this flag. The version-mapping form
          will show a "Deprecated" badge but still allow selecting this URL.
        </span>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => onCancel()} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
